import { ChevronDown, History, KeyRound, Pencil, Plus, Trash2, Users, type LucideIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { fmtDateTime, fmtRelative } from '../../shared/format'
import { useRoleActivity } from '../api'
import type { RoleActivityAction } from '../types'

const ICONS: Record<string, LucideIcon> = {
  ROLE_CREATE: Plus,
  ROLE_UPDATE: Pencil,
  ROLE_PERMISSIONS_UPDATE: KeyRound,
  ROLE_USERS_UPDATE: Users,
  ROLE_DELETE: Trash2,
}

const FILTERS: { value: string; labelKey: string }[] = [
  { value: '', labelKey: 'all' },
  { value: 'ROLE_PERMISSIONS_UPDATE', labelKey: 'permissions' },
  { value: 'ROLE_USERS_UPDATE', labelKey: 'users' },
  { value: 'ROLE_UPDATE', labelKey: 'meta' },
]

export function RoleHistory({ roleId }: { roleId: string }) {
  const { t } = useTranslation()
  const [action, setAction] = useState('')
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<string | null>(null)
  const query = useRoleActivity(roleId, page, action)

  if (query.isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-14" />
        ))}
      </div>
    )
  }

  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />

  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0
  const limit = query.data?.limit ?? 20
  const totalPages = Math.max(1, Math.ceil(total / limit))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            onClick={() => {
              setAction(f.value)
              setPage(1)
            }}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              action === f.value ? 'border-brand-blue/40 bg-brand-blue/10 text-brand-blue' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(`access.history.filters.${f.labelKey}`)}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState icon={History} title={t('access.history.empty')} />
      ) : (
        <ol className="space-y-3">
          {items.map((e) => {
            const Icon = ICONS[e.action] ?? History
            const open = expanded === e.id
            const label = e.actionLabel || t(`access.history.actions.${e.action as RoleActivityAction}`, { defaultValue: e.action })
            const when = fmtRelative(e.createdAt)
            const whenLabel = when === '—' ? fmtDateTime(e.createdAt) : when
            return (
              <li key={e.id} className="rounded-xl border">
                <button type="button" className="flex w-full items-start gap-3 px-3 py-3 text-left" onClick={() => setExpanded(open ? null : e.id)}>
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-card text-brand-blue">
                    <Icon className="size-3.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{label}</span>
                    {e.summary ? <span className="mt-0.5 block text-xs text-muted-foreground">{e.summary}</span> : null}
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {e.actor?.fullName || e.actorEmail || '—'}
                      {e.actor?.email && e.actor.fullName ? ` · ${e.actor.email}` : ''}
                      {' · '}
                      <span title={fmtDateTime(e.createdAt)}>{whenLabel}</span>
                    </span>
                  </span>
                  <ChevronDown className={cn('mt-1 size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
                </button>
                {open ? (
                  <div className="border-t bg-muted/20 px-3 py-3 text-xs">
                    {e.ipAddress ? <p className="mb-2 text-muted-foreground">IP: {e.ipAddress}</p> : null}
                    <pre className="overflow-x-auto rounded-lg bg-card p-3 font-mono text-[11px] whitespace-pre-wrap">{JSON.stringify(e.payload, null, 2)}</pre>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ol>
      )}

      {totalPages > 1 ? (
        <div className="flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            {t('access.history.prev')}
          </Button>
          <span className="text-xs text-muted-foreground">
            {page}/{totalPages}
          </span>
          <Button type="button" size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            {t('access.history.next')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
